import styled, { css, keyframes } from 'styled-components';
import { TColorProp } from 'styled-components';

import { SPACING } from '~/common/constants/spacing';
import { RADIUS } from '~/common/constants/radius';

import { TTextInputSize } from './types';
import {
  getParagraphFontSize,
  getParagraphLetterSpacing,
  getParagraphLineHeight,
} from '../Typograph/Paragraph/styles';

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const GlowWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  padding: 1.5px;
  background: ${({ theme }) => theme.neutral700};
  transition: background 0.4s ease;

  &::before {
    content: '';
    position: absolute;
    top: -100%;
    left: -25%;
    width: 150%;
    height: 400%;
    background: conic-gradient(
      from 0deg,
      transparent 0%,
      transparent 40%,
      #adff02 55%,
      #78d501 65%,
      #adff02 80%,
      transparent 85%,
      transparent 100%
    );
    animation: ${rotate} 3s linear infinite;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover::before,
  &:focus-within::before {
    opacity: 1;
  }

  &:focus-within {
    box-shadow: 0 0 20px rgba(173, 255, 2, 0.15);
  }

  > div {
    border-radius: 12.5px;
    border-color: transparent;
  }
`;

interface IInputWrapperProps {
  $hasError: boolean;
  $size: TTextInputSize;
  $width?: string;
  $isDisabled: boolean;
  $hasPrefixOrSuffix: boolean;
  $isPassword: boolean;
  $withoutBorder: boolean;
  $inputAs: 'input' | 'textarea';
  $textAreaHeight: string | null;
  $backgroundColor: TColorProp;
  $borderColor: TColorProp;
  $onHoverBackgroundColor: TColorProp;
  $onFocusBorderColor: TColorProp;
}

interface ICounterWrapperProps {
  $size: TTextInputSize;
}

export const Container = styled.label<{
  textAreaDynamicFullHeight: boolean;
}>`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACING._4};
  ${({ textAreaDynamicFullHeight }) =>
    textAreaDynamicFullHeight &&
    css`
      height: 100%;
    `}

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
`;

export const LoaderWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;

  width: min-content;
  margin-left: auto;
  > span {
    height: 20px !important;
    width: 20px !important;
  }
`;

export const getTextInputHeight: Record<TTextInputSize, string> = {
  'x-small': '32px',
  small: '36px',
  medium: '40px',
  large: '48px',
};

const getTextInputPadding: Record<TTextInputSize, string> = {
  'x-small': `${SPACING._6}`,
  small: `${SPACING._8}`,
  medium: `${SPACING._10}`,
  large: `${SPACING._10}`,
};

const getTextInputGap: Record<TTextInputSize, string> = {
  'x-small': `${SPACING._6}`,
  small: `${SPACING._8}`,
  medium: `${SPACING._8}`,
  large: `${SPACING._8}`,
};

export const CopySuffixWrapper = styled.div<{
  $size: TTextInputSize;
  $focus: boolean;
}>`
  cursor: pointer;
  border-left: ${({ theme }) => `1px solid ${theme.neutral300}`};
  padding: ${({ $size }) => getTextInputPadding[$size]};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ease-in 0.2s;
  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.neutral950};
    `}
`;

export const PrefixOrSuffixWrapper = styled.div<{
  $size: TTextInputSize;
  $focus: boolean;
  $borderPosition: 'left' | 'right';
  $borderColor?: TColorProp;
  $withoutPrefixDelimiter: boolean;
}>`
  ${({ $borderPosition, $borderColor, $withoutPrefixDelimiter }) =>
    $withoutPrefixDelimiter
      ? css`
          border-left: none;
          border-right: none;
        `
      : $borderPosition === 'right'
      ? css`
          border-right: ${({ theme }) =>
            `1px solid ${
              $borderColor ? theme[$borderColor as string] : theme.neutral700
            }`};
        `
      : css`
          border-left: ${({ theme }) =>
            `1px solid ${
              $borderColor ? theme[$borderColor as string] : theme.neutral700
            }`};
        `}

  padding: ${({ $size }) => getTextInputPadding[$size]};
  ${({ $withoutPrefixDelimiter }) =>
    $withoutPrefixDelimiter &&
    css`
      padding-right: 0;
      padding-left: ${SPACING._4};
    `};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ease-in 0.2s;
  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.neutral950};
    `}
`;

export const InputWrapper = styled.div<IInputWrapperProps>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: ${({ $width }) => $width};
  border-radius: ${RADIUS._8};
  border: ${({ theme, $withoutBorder, $borderColor }) =>
    $withoutBorder ? 'unset' : `1px solid ${theme[$borderColor as string]}`};
  background-color: ${({ theme, $backgroundColor }) =>
    theme[$backgroundColor as string]};
  padding: ${({ $size, $hasPrefixOrSuffix }) =>
    $hasPrefixOrSuffix ? 0 : getTextInputPadding[$size]};
  padding-right: ${({ $isPassword }) => $isPassword && '0 !important'};
  gap: ${({ $size }) => getTextInputGap[$size]};
  transition: all ease-in 0.2s;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 0s 600000s, color 0s 600000s !important;
    appearance: unset !important;
    border: none !important;
    text-decoration: none !important;
    outline: none !important;
  }

  ${({ $hasPrefixOrSuffix, $size }) =>
    $hasPrefixOrSuffix &&
    css`
      input {
        padding: ${getTextInputPadding[$size]};
      }

      #input-icon-id {
        padding: ${getTextInputPadding[$size]};
      }
    `}

  ${({ $inputAs, $size }) =>
    $inputAs === 'input' &&
    css`
      height: ${getTextInputHeight[$size]};
    `}

    &:focus-within {
    border-color: ${({ theme, $onFocusBorderColor }) =>
      theme[$onFocusBorderColor as string]};
  }

  &:hover {
    background-color: ${({ theme, $onHoverBackgroundColor }) =>
      theme[$onHoverBackgroundColor as string]};
  }

  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: ${({ theme }) => theme.red} !important;
    `};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      border-color: ${({ theme }) => theme.neutral300};
      background-color: ${({ theme }) => theme.neutral300};
      cursor: not-allowed;
    `};

  ${({ $textAreaHeight }) =>
    $textAreaHeight &&
    css`
      min-height: ${$textAreaHeight};
    `}
`;

export const CustomInput = styled.input<{
  centralize: boolean;
  textAreaHeight: string | null;
}>`
  background: none;
  background-color: transparent !important;
  width: 100%;
  height: 100%;
  overflow: hidden;
  outline: none;
  border: none;
  flex: 1;
  min-height: 100%;

  ${({ centralize }) =>
    centralize &&
    css`
      text-align: center;
    `}

  line-height: ${getParagraphLineHeight['small']}px;
  font-size: ${getParagraphFontSize['small']}px;
  letter-spacing: ${getParagraphLetterSpacing('small')}px;
  font-weight: 400;
  color: ${({ theme }) => theme.neutral0};

  &::placeholder {
    color: ${({ theme }) => theme.neutral400};
  }

  &:disabled {
    cursor: not-allowed;

    &::placeholder {
      color: ${({ theme }) => theme.neutral600};
    }
  }

  ${({ textAreaHeight }) =>
    textAreaHeight &&
    css`
      min-height: ${textAreaHeight};
      overflow-y: auto;
      scrollbar-width: thin;
    `}
`;

export const CounterWrapper = styled.div<ICounterWrapperProps>`
  position: absolute;
  bottom: ${({ $size }) => `calc(${getTextInputPadding[$size]} - 3px)`};
  right: ${({ $size }) =>
    `calc(${getTextInputPadding[$size]} + 7px + ${SPACING._6})`};

  pointer-events: none;
`;

export const GlowEffect = styled.div`
  display: none;
`;
